import React, { useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';

/**
 * Modal for changing stage of multiple clients
 */
const BulkStageChangeModal = ({ isOpen, onClose, onConfirm, selectedCount, availableStages }) => {
  const [selectedStage, setSelectedStage] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  const handleConfirm = async () => {
    if (!selectedStage) return;

    setIsProcessing(true);
    try {
      await onConfirm(selectedStage);
      setSelectedStage('');
      onClose();
    } catch (error) {
      console.error('Error changing stage:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    if (!isProcessing) {
      setSelectedStage('');
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={handleClose} />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">
              Change Stage for {selectedCount} Client{selectedCount > 1 ? 's' : ''}
            </h2>
            <button
              onClick={handleClose}
              disabled={isProcessing}
              className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Body */}
          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select New Stage
              </label>
              <Select value={selectedStage} onValueChange={setSelectedStage} disabled={isProcessing}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Choose a stage..." />
                </SelectTrigger>
                <SelectContent>
                  {availableStages.map((stage) => (
                    <SelectItem key={stage} value={stage}>
                      {stage}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <p className="text-sm text-gray-600">
              This will update the stage for all {selectedCount} selected client{selectedCount > 1 ? 's' : ''}.
            </p>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={isProcessing}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={!selectedStage || isProcessing}
              className="text-white"
              style={{ backgroundColor: 'var(--cb-orange)' }}
            >
              {isProcessing ? 'Updating...' : 'Change Stage'}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default BulkStageChangeModal;
